<script>
  import { setContext, getContext } from "svelte";
  import { ENTRIES } from "./app_config.js";
  import { page } from '$app/state';
  import t from "$lib/i18n.svelte.js";
  import { fetch_json, entry_url } from "$lib/util.js";
  import { goto } from '$app/navigation';
  import MainPageContent from "$lib/MainPageContent.svelte";
  import AddToCollectionDialog from "$lib/AddToCollectionDialog.svelte";
  import InPlaceEntryEditor from "$lib/InPlaceEntryEditor.svelte";
  import Footer from "$lib/Footer.svelte";
  import SEO from "$lib/SEO.svelte";
  import { invalidateAll } from "$app/navigation";

  let { raw_entry, is_create_mode, is_homepage, additional_entry_data } = $props();
  
  // This is "sort of" considered derived state initially (see effect)
  let entry = $state(new ENTRIES[raw_entry.type].model(raw_entry));
  let site_settings = getContext('site_settings');

  let first_block_background_color = $derived(entry?.body?.blocks?.[0]?.background_color);
  let is_editing = $state(is_create_mode || page.url.searchParams.get('edit') ? true : false);
  let add_to_collection_dialog_open = $state(false);
  let active_container = $state(undefined);
  let contextual_entry_id = $state();
  let previous_scroll_position = $state(0);

  function get_page_title() {
    if (is_create_mode) {
      return t('new_entry',{ entry_type: t(raw_entry.type) });
    } else if (is_homepage) {
      return site_settings.name;
    } else {
      return `${entry.preview.title || 'Untitled'} — ${site_settings.name}`;
    }
  }

  // Call this (from different) places to reset 
  // E.g. when <PlainText> calls this when not provided with an explicit focus property.
  function clear_selection() {
    active_container?.clear_selection();
    active_container = undefined;
  }

  function start_editing() {
    is_editing = true;
    clear_selection();
  }

  function end_contextual_editing() {
    // HACK: we need to reload all entry previews (to reflect the changes made in the in-place editor)
    const blocks = entry.body?.blocks ?? [];
    for (const block of blocks) {
      if (block.type === 'story') {
        // Reload all entry previews that reference the edited entry
        // console.log('YOYOY', block.entry_previews_multiref.includes(contextual_entry_id));
        if (block.entry_previews_multiref.includes(contextual_entry_id)) {
          // HACK: We add a dummy entry_id to trigger a reload of the entry previews
          block.entry_previews_multiref = [...block.entry_previews_multiref, undefined];
        }
      }
    }
    contextual_entry_id = undefined;
    setTimeout(() => {
      window.scrollTo(0, parseInt(previous_scroll_position, 10));
      previous_scroll_position = 0;
    },0);
  }

  function start_contextual_editing(entry_id) {
    previous_scroll_position = window.scrollY || document.documentElement.scrollTop;
    contextual_entry_id = entry_id;
  }

  async function cancel_editing() {
    // HACK: We handle two edge cases here, where someone might have clicked "edit" or "duplicate" on /all
    // Cancel should take you back from where you were coming from.
    const try_history_back = page.url.searchParams.get('edit') || page.url.searchParams.get('from');
    if (try_history_back && window.history.length > 1) {
      return history.back();
    }

    // Use snapshot for potential "roll back" (=cancel editing);
    if (!is_create_mode) {
      await invalidateAll(); // this will replace raw_entry with the source of truth on the server
      entry = new ENTRIES[raw_entry.type].model(raw_entry); // reset to original data
      is_editing = false;
      clear_selection();
    } else {
      // HACK: when start_editing hasn't been called, we know we are in "create mode",
      // so cancel means "abort creation"
      // TODO: find a better way to handle this case
      goto('/');
    }
  }

  async function save() {
    const new_or_updated_entry = entry.to_json();

    if (site_settings.home_page_preview_ref ==  new_or_updated_entry.entry_id && new_or_updated_entry.visibility !== 'public') {
      alert('Sorry. The homepage must be public. Select a different home page first, before you can change the visibility of this entry.');
      return;
    }

    if (!page.data.current_user) {
      alert('Sorry. Only the admin can save changes.');
      return;
    }

    try {
      if (new_or_updated_entry.entry_id) {
        await fetch_json('POST', '/api/update-entry', new_or_updated_entry);
      } else {
        const new_entry = await fetch_json('POST', '/api/create-entry', new_or_updated_entry);
        goto(entry_url(new_entry));
      }
    } catch (err) {
      console.error(err);
      alert('There was an error. You can try again, but before that, please just copy and paste your article into a safe place.');
    }

    is_editing = false;
    clear_selection();
  }

  setContext("entry_api", {
    get is_disabled() {
      return contextual_entry_id ? true : false;
    },
    get page_title() {
      return entry.preview.title;
    },
    get page_description() {
      return entry.preview.description;
    },
    get entry() {
      return entry;
    },
    get active_container() {
      return active_container;
    },
    set active_container(_active_container) {
      // we clear the selection of the other container, when switching
      active_container?.clear_selection();
      active_container = _active_container;
    },
    get is_editing() {
      return is_editing;
    },
    set is_editing(_is_editing) {
      is_editing = _is_editing;
    },
    get add_to_collection_dialog_open() {
      return add_to_collection_dialog_open;
    },
    set add_to_collection_dialog_open(_add_to_collection_dialog_open) {
      add_to_collection_dialog_open = _add_to_collection_dialog_open;
    },
    start_contextual_editing,
    clear_selection,
    cancel_editing,
    start_editing,
    save,
  });

  $effect.pre(() => {
    if (raw_entry.entry_id !== entry.entry_id) {
      entry = new ENTRIES[raw_entry.type].model(raw_entry);
      add_to_collection_dialog_open = false;
    }
  });
</script>

<!-- Currently we fall back to the site description if no description could be extracted from the entry's content. -->
<SEO
  title={get_page_title()}
  description={entry.preview.description || site_settings.description}
  image={entry.preview.image}
  type={entry.type}
  author={site_settings.name}
/>

{@html `<style>:root{--first-block-background-color: ${first_block_background_color}}</style>`}

<div class:hidden={contextual_entry_id !== undefined} >
  <MainPageContent
    id={entry.type + '-' + entry.entry_id}
    {is_editing}
  >
    {@const EntryTypeComponent = ENTRIES[entry.type].component}
    <EntryTypeComponent 
      {additional_entry_data} 
      bind:entry
      {is_editing}
    />    
    {#snippet footer()}
      <Footer />
    {/snippet}
  </MainPageContent>
</div>

{#if contextual_entry_id}
  <InPlaceEntryEditor entry_id={contextual_entry_id} onclose={end_contextual_editing} />
{/if}

{#if add_to_collection_dialog_open}
  <AddToCollectionDialog {entry} collections={page.data.collections} onclose={() => { add_to_collection_dialog_open = false; }} />
{/if}